import { ActionIcon, Button, Card, Group, Stack, Text } from "@mantine/core";
import {
  IconCopy,
  IconEdit,
  IconIdBadge2,
  IconQrcode,
  IconTrash,
} from "@tabler/icons-react";
import { useUpdateBadgeTemplate } from "../../api/badges";
import { PX_PER_MM } from "../builder/constants";
import { toMm, FIELD_LABELS } from "../builder/badge/badgeConstants";

const THUMB_HEIGHT = 220;

const justifyForAlign = (align) =>
  align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start";

// Renders one element, mirroring the builder canvas: text/field show their
// label at their own color/alignment, qr a glyph, rectangle its fill.
function ThumbElement({ el, thumbHeight, nativeHeightPx }) {
  const base = {
    position: "absolute",
    left: `${el.xPct}%`,
    top: `${el.yPct}%`,
    width: `${el.widthPct}%`,
    height: `${el.heightPct}%`,
  };

  if (el.type === "rectangle") {
    return <div style={{ ...base, backgroundColor: el.fill }} />;
  }

  if (el.type === "qr") {
    return (
      <div style={base}>
        <IconQrcode
          style={{ width: "100%", height: "100%" }}
          stroke={1.5}
          color="#000000"
        />
      </div>
    );
  }

  const label =
    el.type === "field" ? FIELD_LABELS[el.fieldKey] || el.fieldKey : el.text;
  // The editor renders the pane at nativeHeightPx with font in px, so a glyph
  // occupies fontSize/nativeHeightPx of the height. Scale by the same ratio so
  // the thumbnail keeps that proportion regardless of badge size.
  const fontSize = Math.max(
    4,
    (el.fontSize || 12) * (thumbHeight / nativeHeightPx)
  );

  return (
    <div
      style={{
        ...base,
        display: "flex",
        alignItems: "center",
        justifyContent: justifyForAlign(el.align),
        color: el.color || "#000000",
        fontSize,
        lineHeight: 1.1,
        overflow: "hidden",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </div>
  );
}

// Renders the badge's elements at their %-positions inside an aspect-correct
// box, so the card shows a real (if tiny) preview of the layout.
export function BadgeThumbnail({
  templateData,
  backgroundUrl,
  height = THUMB_HEIGHT - 16,
}) {
  const size = templateData?.size;
  const elements = templateData?.elements || [];

  if (!size) {
    return (
      <IconIdBadge2
        size={Math.min(48, height)}
        color="var(--mantine-color-gray-5)"
      />
    );
  }

  const heightMm = toMm(size.height, size.unit);
  const ratio = toMm(size.width, size.unit) / heightMm;
  const width = height * ratio;
  const nativeHeightPx = heightMm * PX_PER_MM;

  return (
    <div
      style={{
        width,
        height,
        backgroundColor: "white",
        backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : "none",
        backgroundSize: "100% 100%",
        boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
        position: "relative",
        overflow: "hidden",
        fontFamily: "Montserrat, sans-serif",
      }}
    >
      {elements.map((el) => (
        <ThumbElement
          key={el.id}
          el={el}
          thumbHeight={height}
          nativeHeightPx={nativeHeightPx}
        />
      ))}
    </div>
  );
}

function BadgeCard({ badge, isCheckInBadge, onCopy, onEdit, onDelete }) {
  const { mutate: updateBadge, isPending } = useUpdateBadgeTemplate({
    id: badge.id,
  });

  const togglePrintBadge = () => updateBadge({ isDefault: !isCheckInBadge });

  return (
    <Card shadow="sm" radius="md" withBorder p={0}>
      <Card.Section
        style={{
          height: THUMB_HEIGHT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "var(--mantine-color-gray-1)",
        }}
      >
        <BadgeThumbnail
          templateData={badge.templateData}
          backgroundUrl={badge.backgroundKey}
        />
      </Card.Section>

      <Group justify="space-between" wrap="nowrap" p="md">
        <Stack gap={6} style={{ minWidth: 0 }}>
          <Text fw={600} lineClamp={1}>
            {badge.name}
          </Text>
          <Button
            size="compact-xs"
            radius="xl"
            color={isCheckInBadge ? "teal" : "gray"}
            variant={isCheckInBadge ? "filled" : "outline"}
            loading={isPending}
            onClick={togglePrintBadge}
            style={{ alignSelf: "flex-start" }}
          >
            Print station badge
          </Button>
        </Stack>

        <Group gap="xs" wrap="nowrap">
          <ActionIcon
            variant="light"
            size="lg"
            onClick={() => onCopy(badge)}
            aria-label={`Copy ${badge.name}`}
          >
            <IconCopy size={18} />
          </ActionIcon>
          <ActionIcon variant="light" size="lg" onClick={() => onEdit(badge)}>
            <IconEdit size={18} />
          </ActionIcon>
          <ActionIcon
            variant="light"
            color="red"
            size="lg"
            onClick={() => onDelete(badge)}
          >
            <IconTrash size={18} />
          </ActionIcon>
        </Group>
      </Group>
    </Card>
  );
}

export default BadgeCard;
